<script setup lang="ts">
interface Season {
  name: string;
  months: string;
  image: string;
  image_alt?: string;
  temperature?: string;
  highlights: string[];
  avoid?: string[];
}

interface SeasonalGuideBlock {
  title: string;
  intro?: string;
  seasons: Season[];
}

defineProps<{
  block: SeasonalGuideBlock;
}>();

const { t } = useI18n();
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-lime-50/30 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
    aria-labelledby="seasonal-guide-heading"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-amber-400/10 blur-3xl dark:bg-amber-400/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
    />

    <div class="relative mx-auto w-full max-w-7xl px-4 py-8 md:py-12">
      <header data-reveal class="mb-6 max-w-3xl md:mb-8">
        <div
          class="flex items-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500"
        >
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
          <span>{{ t("location.seasons.eyebrow") }}</span>
        </div>
        <h2
          id="seasonal-guide-heading"
          class="mt-4 font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.title }}
        </h2>
        <p
          v-if="block.intro"
          class="mt-5 font-body text-base text-slate-600 md:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <ul
        data-reveal
        class="-mx-4 flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-6 scroll-smooth md:mx-0 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 md:pb-0 lg:grid-cols-4"
      >
        <li
          v-for="(season, i) in block.seasons"
          :key="i"
          class="group relative flex w-[82%] shrink-0 snap-start flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-slate-900/5 shadow-lg shadow-slate-900/5 sm:w-[60%] md:w-auto md:shrink dark:bg-slate-900 dark:ring-white/10"
        >
          <div class="relative aspect-[4/5] w-full overflow-hidden">
            <NuxtImg
              :src="season.image"
              :alt="season.image_alt || season.name"
              width="600"
              height="750"
              format="webp"
              loading="lazy"
              sizes="xs:80vw sm:60vw md:50vw lg:25vw"
              class="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-[1.06]"
            />
            <div
              aria-hidden="true"
              class="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"
            />
            <div class="absolute inset-x-0 bottom-0 p-5 md:p-6">
              <div
                class="flex items-baseline justify-between gap-2 font-body text-[11px] uppercase tracking-[0.22em] text-amber-300"
              >
                <span>{{ season.months }}</span>
                <span v-if="season.temperature" class="tabular-nums text-white/85">
                  {{ season.temperature }}
                </span>
              </div>
              <h3
                class="mt-1 font-display text-3xl italic leading-tight text-white drop-shadow md:text-4xl"
              >
                {{ season.name }}
              </h3>
            </div>
          </div>

          <div class="flex flex-1 flex-col gap-4 p-5 md:p-6">
            <ul class="space-y-2">
              <li
                v-for="(h, hi) in season.highlights"
                :key="hi"
                class="flex items-start gap-2.5 font-body text-sm text-slate-700 dark:text-slate-200"
              >
                <Icon
                  name="tabler:check"
                  size="16"
                  class="mt-0.5 shrink-0 text-amber-500 dark:text-amber-400"
                  aria-hidden="true"
                />
                <span>{{ h }}</span>
              </li>
            </ul>

            <ul
              v-if="season.avoid && season.avoid.length"
              class="space-y-2 border-t border-slate-200 pt-4 dark:border-slate-700/60"
            >
              <li
                v-for="(a, ai) in season.avoid"
                :key="ai"
                class="flex items-start gap-2.5 font-body text-sm text-slate-500 dark:text-slate-400"
              >
                <Icon
                  name="tabler:x"
                  size="16"
                  class="mt-0.5 shrink-0 text-slate-400 dark:text-slate-500"
                  aria-hidden="true"
                />
                <span>{{ a }}</span>
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>
